/* eslint-disable react/jsx-no-useless-fragment */
import React, { Fragment } from 'react';
import styled, { css } from 'styled-components';
import {
  arrayOf, shape, element, bool, string, func, number,
} from 'prop-types';
import PageConfigCacheContainer from 'containers/PageConfigCacheContainer';
import SupportButtonStateContainer from 'containers/SupportButtonStateContainer';
import withSubscribe from 'containers/withSubscribe';
import SeoHead from 'components/seo/SeoHead';
import Freeze from 'components/common/Freeze';
import ErrorPage from 'pages/ErrorPage';
import Signup from 'components/auth/Signup';
import withApplicationConfig from 'containers/withApplicationConfig';
import {
  loginPopupLogoutSuccessfulEvent, loginProfileButtonClickEvent,
  loginPopupLoginSuccessfulEvent, withTracking, navBarFooterTrackingEvent,
} from 'components/tracking';
import { withRouter } from 'react-router';
import { withLogout } from 'containers/withQueries';
import {
  getRuntimeEnvVariables,
  disableBodyScrolling,
  includesURL,
  getSignupExitPopupHasBeenShowed,
  setSignupExitPopupHasBeenShowed,
  getMobileExitPopupHasBeenShown,
  setMobileExitPopupHasBeenShown,
  getComponentConfig,
  isShowExitPopup,
} from 'helpers/Utils';
import AccountMenu from 'components/layout/AccountMenu';
import NotSupportBanner from 'components/NotSupportBanner/NotSupportBanner';
import {
  Loading as LoadingPage, Footer, HtmlParser,
  Modal as CagModal,
  ModalContentType,
  ModulabTypography, ModulabTypographyVariant,
  TYPOGRAPHY_COLORS,
  sizes,
} from '@hyphengroup/cag-components';
import { CustomerProfileContext } from 'containers/withCustomerProfileContext';
import SignupExitModal from 'components/layout/SignupExitModal/SignupExitModal';
import withUserAgent from 'containers/withUserAgent';
import { exitModalPageLoadEvent } from 'components/tracking/ExitModalTrackingUtils';
import SignupBtnsExperiment from 'components/auth/SignupBtnsExperiment';
import NavBar from './NavBar/NavBar';

const Container = styled.div`
${({ variant, style }) => `
  ${style}
  width: 100%;
  position: relative;

  ${variant !== 'containSticky' && css`
    ${style?.overflowX ?? '& { overflow-x: hidden; }'}
  `}
  ${variant === 'navBarSticky' && css`
    & {
      height: 100vh;
      height: calc(var(--vh, 1vh) * 100);
    }
  `}
  ${variant === 'fullScreen' && css`
    & {
      height: 100vh;
      display: flex;
      flex-flow: column;
    }
  `}
`}`;

const ContentContainer = styled.div`
  ${({ variant }) => `
  ${variant === 'fullScreen' && css`
    & { flex: 1; }
  `}
`}`;

class Layout extends React.Component {
  constructor(props) {
    super(props);
    const { subscribedContainers, customerProfile, defaultAuthLocation } = props;
    const [pageConfigCache] = subscribedContainers;
    pageConfigCache.cachePageConfig(props);
    this.state = {
      hasError: false,
      isSignupModalShown: false,
      isMobileMenuShown: false,
      isAccountMenuShown: false,
      isSignupExitModalOpen: false,
      isMobileExitModalOpen: false,
      isMobileIdleModalOpen: false,
      isSpinnerShown: false,
      loginState: customerProfile?.loginState,
      authLocation: defaultAuthLocation,
      navBarRef: React.createRef(),
    };
  }

  componentDidCatch(error, info) {
    console.log('error', error, info);
    // Display fallback UI
    this.setState({ hasError: true });
    // You can also log the error to an error reporting service
    // logErrorToMyService(error, info);
  }

  componentDidMount() {
    const { isSticky, variant } = this.props;
    if (isSticky && variant === 'navBarSticky') this.calcWebViewHeight();
  }

  static getDerivedStateFromProps(props, currentState) {
    const { customerProfile } = props;
    const { isSignupModalShown, loginState } = currentState;

    if (customerProfile?.loginState !== loginState) {
      const newState = {
        ...currentState,
        loginState: customerProfile?.loginState,
      };

      if (customerProfile?.loginState === 'LOGGED_IN' && isSignupModalShown) {
        return {
          ...newState,
          isSignupModalShown: false,
        };
      }

      return newState;
    }

    return null;
  }

  setIsMobileIdleModalOpen = (isOpen) => {
    const { isMobileIdleModalOpen } = this.state;
    if (isMobileIdleModalOpen !== isOpen && !this.isAnyPopupDisplaying()) {
      this.setState({
        isMobileIdleModalOpen: isOpen,
      });
    }
  }

  isAnyPopupDisplaying = () => {
    const { isMobileExitModalOpen, isSignupExitModalOpen } = this.state;
    return isMobileExitModalOpen || isSignupExitModalOpen;
  }

  showSignup = ({ postLoginOpToken, useRewardsModal, authLocation } = {}) => {
    const { hasStandaloneLoginPage } = this.props;
    if (hasStandaloneLoginPage) {
      const redirectUri = encodeURIComponent(window.location.href);
      window.location.href = `/login?redirect_uri=${redirectUri}`;
    } else {
      const { isSignupModalShown } = this.state;
      if (!isSignupModalShown) {
        this.setState({
        isSignupModalShown: true,
        postLoginOpToken,
          useRewardsModal,
          authLocation,
        });
      }
    }
  }

  hideSignup = () => {
    const { isSignupModalShown } = this.state;

    isSignupModalShown && this.setState({ isSignupModalShown: false });
  }

  showExitModal = (verticalEnum) => {
    // eslint-disable-next-line eqeqeq
    const enableSignupExitModal = getRuntimeEnvVariables('RUNTIME_ENABLE_SIGNUP_EXIT_MODAL') == 'true';
    const {
      track,
      exitPopupOrder,
      userAgent,
      language,
    } = this.props;
    const { loginState } = this.state;
    const isLoggedIn = loginState === 'LOGGED_IN';
    let isStop = false;
    for (let i = 0; i < exitPopupOrder.length; i += 1) {
      if (isShowExitPopup(userAgent, verticalEnum, exitPopupOrder[i])) {
        switch (exitPopupOrder[i].name) {
          case 'signupExitPopup':
            isStop = this.showSignupExitPopup(isLoggedIn, track, language,
              enableSignupExitModal);
            break;
          case 'mobileExitPopup':
            isStop = this.showMobileExitPopup(verticalEnum);
            break;
          default:
            break;
        }
      }
      if (isStop) return;
    }
  }

  showSignupExitPopup = (isLoggedIn, track,
    language, enableSignupExitModal) => {
    if (enableSignupExitModal && !isLoggedIn && !getSignupExitPopupHasBeenShowed()) {
      exitModalPageLoadEvent(track, language);
      this.setState({
        isSignupExitModalOpen: true,
      });
      setSignupExitPopupHasBeenShowed(true);
      return true;
    }
    return false;
  }

  showMobileExitPopup = () => {
    if (!getMobileExitPopupHasBeenShown()) {
      this.setState({
        isMobileExitModalOpen: true,
      });
      setMobileExitPopupHasBeenShown(true);
      return true;
    }
    return false;
  }

  hideSignupExitModal = () => {
    this.setState({
      isSignupExitModalOpen: false,
    });
  }

  goTo = (url) => {
    const { hideSignup, hideAccountMenu } = this;
    const { history, refetch } = this.props;

    const redirectUrl = url || '/';
    if (history.location.pathname !== redirectUrl) {
      history.push(redirectUrl);
    } else {
      refetch();
      hideSignup();
      hideAccountMenu();
    }
    disableBodyScrolling(false);
  }

  logout = ({ homePageURL }) => {
    const {
      logoutMutation, track,
    } = this.props;

    const keycloakLogoutUrl = getRuntimeEnvVariables('RUNTIME_AUTH_LOGOUT_ENDPOINT');

    if (keycloakLogoutUrl) {
      fetch(new URL(keycloakLogoutUrl, window.location.href), { method: 'POST', credentials: 'include' })
        .then((response) => {
          if (response.ok) {
            window.top.location.href = !homePageURL ? '/' : homePageURL;
          } else {
            console.error('Logout failed:', response.statusText);
          }
        })
        .catch((error) => {
          console.error('Error during logout:', error.message);
        });
    } else {
      logoutMutation().then(({ data: { logout: isLogout } }) => {
        if (isLogout) {
          loginPopupLogoutSuccessfulEvent(track);
          window.top.location.href = !homePageURL ? '/' : homePageURL;
        }
      })
        .catch((error) => {
          throw new Error(error);
        });
    }
  }

  showAccountMenu = () => {
    const { hasStandaloneLoginPage } = this.props;
    if (hasStandaloneLoginPage) {
      const redirectUri = encodeURIComponent(window.location.href);
      window.location.href = `/login?redirect_uri=${redirectUri}`;
    } else {
      this.setState({
        isAccountMenuShown: true,
        isMobileMenuShown: false,
      });
      disableBodyScrolling(true);

      const { track } = this.props;
      loginProfileButtonClickEvent(track);
    }
  }

  hideAccountMenu = () => {
    this.setState({ isAccountMenuShown: false });
    disableBodyScrolling(false);
  }

  hideMobileMenu = () => {
    this.setState({ isMobileMenuShown: false });
    disableBodyScrolling(false);
  }

  showMobileMenu = () => {
    const { hasStandaloneLoginPage } = this.props;
    if (hasStandaloneLoginPage) {
      const redirectUri = encodeURIComponent(window.location.href);
      window.location.href = `/login?redirect_uri=${redirectUri}`;
    } else {
      this.setState({
        isMobileMenuShown: true,
        isAccountMenuShown: false,
      });
      disableBodyScrolling(true);
    }
  }

  calcWebViewHeight = () => {
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
  }

  render() {
    const {
      subscribedContainers,
      header,
      seoMetadata,
      footer,
      isFooterShown,
      children,
      blocked,
      freeze,
      language,
      isNavPanelHidden,
      onlyForLoggedInUser,
      hideAccountMenuRoutes,
      location,
      notSupportBanner,
      variant,
      track,
      staticText,
      images,
      refetch,
      customerProfile,
      style,
      onSuccessEvent,
      redirectToDashboard,
      userAgent,
      componentConfig,
      history,
      isSticky,
      faqs,
      faqSeoIndexes,
      isAccountMenuHiddenOverride,
      pathList,
      newIconText,
    } = this.props;
    const exitModal = getComponentConfig({ componentConfig }).EXIT_MODAL;
    const idleModal = getComponentConfig({ componentConfig }).IDLE_MODAL;
    const {
      hasError, isSignupModalShown, isMobileMenuShown, isAccountMenuShown,
      isSpinnerShown, loginState, postLoginOpToken, useRewardsModal,
      isSignupExitModalOpen, authLocation, navBarRef,
      isMobileExitModalOpen, isMobileIdleModalOpen,
    } = this.state;
    const {
      showMobileMenu, hideMobileMenu, showAccountMenu, logout,
      hideAccountMenu, hideSignup, showSignup, goTo, showExitModal,
      hideSignupExitModal, setIsMobileIdleModalOpen,
    } = this;
    const [pageConfigCache, supportButtonState] = subscribedContainers;
    const {
      header: cachedHeader,
      footer: cachedFooter,
    } = pageConfigCache.state;

    // eslint-disable-next-line eqeqeq
    const isLoginEnabled = getRuntimeEnvVariables('RUNTIME_ENABLE_LOGIN') == 'true';
    let baseURL = header?.logo?.link?.href || '';
    if (baseURL === '/') {
      baseURL = '';
    }
    const isAccountMenuHidden = typeof isAccountMenuHiddenOverride === 'boolean' ? isAccountMenuHiddenOverride : includesURL(hideAccountMenuRoutes, location, language);

    const { customer } = customerProfile || {};
    const isLoggedIn = loginState === 'LOGGED_IN';
    track({ data: { loginState: isLoggedIn ? 'yes' : 'no' } });
    supportButtonState.state.isLoggedIn = isLoggedIn;
    supportButtonState.state.firstName = customer?.firstName || '';
    supportButtonState.state.familyName = customer?.familyName || '';
    supportButtonState.state.email = customer?.email || '';

    const footerProps = { ...(footer || cachedFooter) };
    footerProps.footer = <HtmlParser>{footerProps?.copyright}</HtmlParser>;
    footerProps.siteLogo = {};
    footerProps.siteLogo.link = footerProps?.logo?.link?.href;
    footerProps.siteLogo.imageTitle = footerProps?.logo?.link?.title;
    footerProps.siteLogo.imageUrl = `${footerProps?.logo?.image?.url}?auto=webp`;
    footerProps.siteLogo.onClick = () => navBarFooterTrackingEvent(track, 'click', 'logo', '0', 'footer');
    footerProps.base = 'refapp';
    footerProps.links = (footerProps?.socialMedia?.iconLink || []).map((each) => ({
      name: each.icon.toUpperCase(),
      link: `${each.link.href}?auto=webp`,
    }));
    footerProps.appVersion = process.env.RAZZLE_APP_VERSION;
    if (footerProps.column) {
      footerProps.column.forEach((column, colIdx) => {
        let subMenuIdx = 0;
        column?.section.forEach(section => {
          section?.subLinks.forEach((link) => {
            const newLink = link;
            const position = `${colIdx + 1}.${subMenuIdx + 1}`;
            newLink.onClick = () => navBarFooterTrackingEvent(track, 'click', `${section?.link?.title}_${link?.title}`, position, 'footer');
            subMenuIdx += 1;
          });
        });
      });
    }

    if (isLoginEnabled && onlyForLoggedInUser && !isLoggedIn) {
      if (isSpinnerShown) {
        return <LoadingPage />;
      }

      showSignup();
      return (
        <SignupBtnsExperiment>
          {({ btnsOrder }) => (
            <Signup
              isOpen={isSignupModalShown}
              onClose={!onlyForLoggedInUser && hideSignup}
              postLoginOpToken={postLoginOpToken}
              useRewardsModal={useRewardsModal}
              onSuccess={() => {
                if (onSuccessEvent) {
                  loginPopupLoginSuccessfulEvent(track);
                  onSuccessEvent();
                } else {
                  hideSignup();
                  loginPopupLoginSuccessfulEvent(track);
                  if (redirectToDashboard && !isAccountMenuHidden) {
                    goTo(`${baseURL}/${staticText.DASHBOARD_ROUTE_NAME}`);
                  } else {
                    refetch();
                  }
                }
              }}
              staticText={staticText}
              images={images}
              onSocialPopupCallbackSuccess={showSignup}
              signupBtnsOrder={btnsOrder}
              showSkip={false}
            />
          )}

        </SignupBtnsExperiment>
      );
    }

    if (pathList) {
      pathList.forEach(path => {
        if (header) {
          const menu = header.menu?.menuItems.filter(m => m.link?.href === path);
          if (menu?.length > 0) {
            menu[0].newIconText = newIconText;
          }
        } else {
          const menu = cachedHeader?.menu?.menuItems.filter(m => m.link?.href === path);
          if (menu?.length > 0) {
            menu[0].newIconText = newIconText;
          }
        }
      });
    }

    const headerProps = { ...(header || cachedHeader) };
    if (headerProps) {
      headerProps.menu?.menuItems.forEach((menu, menuIdx) => {
        const newMenu = menu;
        if (newMenu && newMenu.link) {
          newMenu.link.onClick = () => navBarFooterTrackingEvent(track, 'click', `${menu?.link?.title}`, `${menuIdx + 1}.0`, 'top-nav');
        }
        menu?.subLinks.forEach((subLink, subIdx) => {
          const newSubLink = subLink;
          if (newSubLink) {
            newSubLink.onClick = () => navBarFooterTrackingEvent(track, 'click', `${menu?.link?.title}_${newSubLink?.title}`, `${menuIdx + 1}.${subIdx + 1}`, 'top-nav');
          }
        });
      });
      if (headerProps.logo) {
        headerProps.logo.onClick = () => navBarFooterTrackingEvent(track, 'click', 'logo', '0', 'top-nav');
      }
    }
    return (
      <Container variant={variant} style={style}>
        {seoMetadata && <SeoHead {...seoMetadata} faqs={faqs} faqSeoIndexes={faqSeoIndexes} />}
        {notSupportBanner?.show && notSupportBanner?.[language] && (
          <NotSupportBanner text={notSupportBanner[language].text} />
        )}
        {(header || cachedHeader) && (
        <NavBar
          navBarRef={navBarRef}
          header={headerProps}
          customerProfile={customerProfile}
          isAccountMenuHidden={isAccountMenuHidden}
          isLoginEnabled={isLoginEnabled}
          isNavPanelHidden={isNavPanelHidden}
          isMobileMenuShown={isMobileMenuShown}
          isSticky={isSticky}
          location={location}
          showMobileMenu={showMobileMenu}
          showSignup={showSignup}
          showAccountMenu={showAccountMenu}
          hideMobileMenu={hideMobileMenu}
          componentConfig={componentConfig}
          baseURL={baseURL}
          track={track}
          history={history}
          language={language}
        />
        )}
        {hasError ? (
          <ErrorPage />
        ) : (
          <CustomerProfileContext.Provider value={{
            ...customerProfile,
            showMobileMenu,
            showAccountMenu,
            showSignup,
            showExitModal,
            baseURL,
            navBarRef,
            isMobileMenuShown,
            setIsMobileIdleModalOpen,
          }}
          >
            <ContentContainer variant={variant}>
              {children}
            </ContentContainer>
          </CustomerProfileContext.Provider>
        )}
        {(footer || cachedFooter) && isFooterShown && (
        <Freeze freeze={freeze}>
          <Footer
            {...footerProps}
            columns={footerProps.column}
            blocked={blocked}
          />
        </Freeze>
        )}
        <SignupBtnsExperiment>
          {({ btnsOrder }) => (
            isSignupModalShown && (
            <Signup
              isOpen={isSignupModalShown}
              onClose={hideSignup}
              onSuccess={() => {
                if (onSuccessEvent) {
                  loginPopupLoginSuccessfulEvent(track);
                  onSuccessEvent();
                } else {
                  hideSignup();
                  loginPopupLoginSuccessfulEvent(track);
                  !isAccountMenuHidden ? goTo(`${baseURL}/${staticText.DASHBOARD_ROUTE_NAME}`) : refetch();
                }
              }}
              staticText={staticText}
              images={images}
              startLocation={authLocation}
              customerEmail={customer?.email}
              postLoginOpToken={postLoginOpToken}
              onSocialPopupCallbackSuccess={showSignup}
              useRewardsModal={useRewardsModal}
              signupBtnsOrder={btnsOrder}
              showSkip={false}
            />
            )
          )}
        </SignupBtnsExperiment>
        {isLoginEnabled && !isAccountMenuHidden && isLoggedIn && (
          <AccountMenu
            isAccountMenuShown={isAccountMenuShown}
            onLogoutClick={() => logout({ homePageURL: baseURL })}
            showMobileMenu={showMobileMenu}
            hideAccountMenu={hideAccountMenu}
            logo={{
              link: { href: baseURL || '/' },
              title: '',
              ...(headerProps?.logo || {}),
              imageUrl: `${footerProps?.logo?.image?.url || ''}?auto=webp`,
            }}
            staticText={staticText}
            customerEmail={customer?.email}
            homePageURL={baseURL}
          />
        )}
        {
          isSignupExitModalOpen && (
            <SignupExitModal
              hideSignupExitModal={hideSignupExitModal}
              isSignupExitModalOpen={isSignupExitModalOpen}
              showSignup={showSignup}
              userAgent={userAgent}
              baseURL={baseURL}
              componentConfig={componentConfig}
              track={track}
              language={language}
            />
          )
        }
        {
          isMobileExitModalOpen && (
            <CagModal
              onClose={() => this.setState({ isMobileExitModalOpen: false })}
              isOpen={isMobileExitModalOpen}
              contentList={[
                {
                  type: ModalContentType.TITLE,
                  contentName: 'top title',
                  title: exitModal?.MODAL_TITLE?.text,
                },
                {
                  type: ModalContentType.IMAGE,
                  contentName: 'top image',
                  image: {
                    title: 'image',
                    url: exitModal?.MODAL_IMAGE?.image?.url,
                    width: sizes(21),
                    height: sizes(18),
                  },
                },
                {
                  type: ModalContentType.BODY,
                  contentName: 'top body',
                  body: (
                    <ModulabTypography dataSelector="dataSelector" variant={ModulabTypographyVariant.BODY_SMALL} color={TYPOGRAPHY_COLORS.TEXT_02} textAlign="center" as="div">
                      <HtmlParser>
                        {exitModal?.MODAL_CONTENT_DESCRIPTION?.text}
                      </HtmlParser>
                    </ModulabTypography>),
                },
              ]}
            />
          )
        }
        {
          isMobileIdleModalOpen && (
            <CagModal
              onClose={() => this.setState({ isMobileIdleModalOpen: false })}
              isOpen={isMobileIdleModalOpen}
              contentList={[
                {
                  type: 'title',
                  contentName: 'top title',
                  title: idleModal?.MODAL_TITLE?.text,
                },
                {
                  type: 'image',
                  contentName: 'top image',
                  image: {
                    title: 'image',
                    url: idleModal?.MODAL_IMAGE?.image?.url,
                    width: sizes(18),
                    height: sizes(15),
                  },
                },
                {
                  type: 'body',
                  contentName: 'top body',
                  body: (
                    <ModulabTypography dataSelector="dataSelector" variant={ModulabTypographyVariant.BODY_SMALL} color={TYPOGRAPHY_COLORS.TEXT_02} textAlign="center" as="div">
                      <HtmlParser>
                        {idleModal?.MODAL_CONTENT_DESCRIPTION?.text}
                      </HtmlParser>
                    </ModulabTypography>),
                },
              ]}
            />
          )
        }
      </Container>
    );
  }
}

Layout.propTypes = {
  seoMetadata: shape({}),
  header: shape({}),
  footer: shape({}),
  isFooterShown: bool,
  subscribedContainers: arrayOf(shape({})),
  children: element,
  blocked: bool,
  freeze: bool,
  localisation: shape({}).isRequired,
  defaultLanguage: string.isRequired,
  language: string.isRequired,
  match: shape({
    params: shape({
      language: string,
    }).isRequired,
  }).isRequired,
  history: shape({
    push: func.isRequired,
  }).isRequired,
  isNavPanelHidden: bool,
  isMobileMenuShown: bool,
  toggleMobileMenu: func,
  isAccountMenuShown: bool,
  toggleAccountMenu: func,
  logoutMutation: func.isRequired,
  onlyForLoggedInUser: bool,
  hideAccountMenuRoutes: arrayOf(string),
  location: shape({
    pathname: string.isRequired,
  }).isRequired,
  contentstackKeys: shape({
    staticTextKeys: shape({
      accountMenu: arrayOf(string).isRequired,
    }).isRequired,
  }).isRequired,
  notSupportBanner: shape({
    show: bool,
    text: string,
  }),
  variant: string,
  track: func,
  defaultAuthLocation: string,
  staticText: shape({}),
  images: shape({}),
  refetch: func.isRequired,
  customerProfile: shape({
    loginState: string.isRequired,
    customer: shape({
      firstName: string,
      familyName: string,
      email: string,
    }),
  }),
  style: shape({}),
  onSuccessEvent: func,
  // redirect users to dashboard after successfully login
  redirectToDashboard: bool,
  userAgent: shape({
    isMobile: bool,
  }),
  componentConfig: arrayOf(shape({})),
  isSticky: bool,
  faqs: arrayOf(shape({
    question: string.isRequired,
    answer: string.isRequired,
  }).isRequired),
  faqSeoIndexes: arrayOf(number),
  // if set the value will overide isAccountMenuHidden
  isAccountMenuHiddenOverride: bool,
  pathList: arrayOf(string),
  newIconText: string,
  exitPopupOrder: arrayOf(shape({
    name: string,
    showDesktop: bool,
    showMobile: bool,
    verticals: arrayOf(string),
  })),
  hasStandaloneLoginPage: bool,
};

Layout.defaultProps = {
  seoMetadata: null,
  header: null,
  footer: null,
  isFooterShown: true,
  subscribedContainers: null,
  children: <Fragment />,
  blocked: false,
  freeze: true,
  isNavPanelHidden: false,
  isMobileMenuShown: null,
  isAccountMenuShown: null,
  toggleMobileMenu: null,
  toggleAccountMenu: null,
  onlyForLoggedInUser: false,
  variant: '',
  track: null,
  defaultAuthLocation: null,
  onSuccessEvent: null,
  redirectToDashboard: true,
  isSticky: false,
  isAccountMenuHiddenOverride: null,
  pathList: null,
  newIconText: 'New',
  style: {},
  hasStandaloneLoginPage: false,
};

export default withTracking(
  withApplicationConfig(
    withRouter(
      withSubscribe(
        withUserAgent(
          withLogout(Layout)
        )
      )([PageConfigCacheContainer, SupportButtonStateContainer])
    ),
    ['languages', 'contentstackKeys', 'accountMenu', 'browserSupport', 'newIconNavbar', 'exitPopupOrder']
  )
);
